import EligibilityTypes from '@/enums/EligibilityTypes';
import { CoverComposition, MedicareCardType, State } from '@general/js/enums';
import { MedicareCardTypeCodeEnum } from '@general/js/enums/MedicareCardTypeEnum';
import { removeWhitespaceFromString, formatDate, reverseFormatFormDate } from '@general/js/utils';
/**
 * Gets the CRM memberNumber from form context (getContext)
 * @returns member number if found, null if not found
 */
export const getCrmMemberNumber = () => {
  if (window.parent.getContext) {
    if (window.parent.getContext.getAttribute('dhl_membershipid')) {
      return window.parent.getContext.getAttribute('dhl_membershipid').getValue();
    }
  }
  return null;
};

/**
 * Finds the url parameters from CRM
 * @returns the url params on CRM or empty object if no parameter is found
 */
export const getCRMUrlParameters = () => {
  const queryString = window.location.search.substring(1);
  const params = {};
  const queryStringParts = queryString.split('&');
  for (let i = 0; i < queryStringParts.length; i++) {
    const pieces = queryStringParts[i].split('=');
    params[pieces[0].toLowerCase()] = pieces.length === 1 ? null : decodeURIComponent(pieces[1]);
  }

  return params;
};

/**
 * Converts CRM code to the corresponding value
 * @param {array} allCodes - array of codes/codenames
 * @param {any} currentCode - current code to match to
 * @returns matching value to the currentCode, otherwise returns the currentCode passed to the function
 */
export const convertCodeToValue = (allCodes, currentCode) => {
  if (allCodes.length > 0) {
    for (let index = 0; index < allCodes.length; index++) {
      if (allCodes[index].code === currentCode) {
        return allCodes[index].value;
      }
    }
  }
  return currentCode;
};

/**
 * Map cover composition to scale flex code based on https://defencehealthlimited.atlassian.net/wiki/spaces/DELTA/pages/44171279/Scale+Flex+Code
 * @param {string} coverComposition
 * @returns {string} scale flex code description
 */
export const mapCompositionToScaleDesc = (coverComposition) => {
  switch (coverComposition) {
    case CoverComposition.SINGLE:
      return 'Single (One Adult)';
    case CoverComposition.COUPLE:
      return 'Couple (Two Adults)';
    case CoverComposition.FAMILY:
      return 'Family (Two Adults with Kids)';
    case CoverComposition.SINGLE_PARENT:
      return 'Single Parent (One Adult with Kids)';
    case CoverComposition.CHILDREN_ONLY:
      return 'Kids Only';
    case CoverComposition.FAMILY_PLUS:
      return 'Family+ (Two Adults with at-least one Non-Student dependent)';
    case CoverComposition.SINGLE_PARENT_PLUS:
      return 'Single Parent+ (One Adult with at-least one Non-Student dependent)';
    default:
      return '';
  }
};

/**
 * Converts CRM name to the corresponding code
 * @param {array} allCodes - array of codes/codenames
 * @param {any} currentName - current name to match to
 * @returns matching value to the currentName, otherwise returns the currentName passed to the function
 */
export const convertNameToCode = (allCodes, currentName) => {
  if (allCodes.length > 0) {
    for (let index = 0; index < allCodes.length; index++) {
      if (allCodes[index].value === currentName) {
        return allCodes[index].code;
      }
    }
  }
  return currentName;
};

/**
 * Convert medicare card type into short code or long description based on `returnShortCode` flag.
 * @param  {string} cardType - medicare card type, can be the full name or short code (eg. 'Blue (Interim)' or 'B')
 * @param  {Boolean} [returnShortCode=true] - If true, returns short code ('B', 'G', 'Y'). If false, returns full name. Defaults to `true`.
 * @returns {string|null} - either short code or long description for the passed-in medicare card type.
 */
export const mapMedicareCardCode = (cardType, returnShortCode = true) => {
  // Only for get price api, the card type will use the long description
  switch (cardType) {
    case MedicareCardType.GREEN:
    case MedicareCardTypeCodeEnum.GREEN:
      return returnShortCode ? MedicareCardTypeCodeEnum.GREEN : MedicareCardType.GREEN;
    case MedicareCardType.BLUE_INTERIM:
    case MedicareCardTypeCodeEnum.BLUE_INTERIM:
      return returnShortCode ? MedicareCardTypeCodeEnum.BLUE_INTERIM : MedicareCardType.BLUE_INTERIM;
    case MedicareCardType.YELLOW_RECIPROCAL:
    case MedicareCardTypeCodeEnum.YELLOW_RECIPROCAL:
      return returnShortCode ? MedicareCardTypeCodeEnum.YELLOW_RECIPROCAL : MedicareCardType.YELLOW_RECIPROCAL;
    default:
      return null;
  }
};

/**
 * Return current rebate applicant medicare card details
 * @param {object} eaiApiResponse, fetched 'dhl_eaiapiresponse' data from CRM context for selected membership.
 * @param {boolean} [formatExpiryDate=true] If true, will format medicare card to DD / MM / YYYY. If false, will keep current format from eaiApiResponse
 * @returns {object} Medicare card details
 */
export const getMedicareCardDetails = (eaiApiResponse, formatExpiryDate = true) => {
  if (eaiApiResponse == null || eaiApiResponse.rebateApplicant == null) {
    return {};
  }

  let medicareCardData = {};

  // Potentially in future to include additional adults that can contribute to the policy
  const rebateApplicantList = ['mainPolicyHolder', 'partner'];
  const rebateApplicant = rebateApplicantList.find((person) => {
    return eaiApiResponse[person].personCode === eaiApiResponse.rebateApplicant;
  });
  const medicareCard = eaiApiResponse[rebateApplicant] ? eaiApiResponse[rebateApplicant].medicareCard : null;

  if (medicareCard != null) {
    medicareCardData = {
      id: medicareCard.id != null ? medicareCard.id : '',
      cardType: mapMedicareCardCode(medicareCard.cardType),
      cardNumber: removeWhitespaceFromString(medicareCard.cardNumber),
      firstName: medicareCard.firstName != null ? medicareCard.firstName : '',
      lastName: medicareCard.lastName != null ? medicareCard.lastName : '',
      referenceNumber: medicareCard.referenceNumber != null ? medicareCard.referenceNumber : '',
    };

    if (medicareCard.expiryDate != null) {
      medicareCardData.expiryDate = formatExpiryDate ? reverseFormatFormDate(formatDate(medicareCard.expiryDate)) : medicareCard.expiryDate;
    }
  }

  return medicareCardData;
};

/**
 * Convert dhl_state code from CRM to Australian state name
 * @param {Number} stateCode Value of dhl_state CRM enum. Eg, 1, 2
 * @returns {string} State name for the given code. Eg, VIC, WA, etc
 */
export const mapCrmStateCodeToName = (stateCode) => {
  let currentState = null;
  switch (stateCode) {
    case 1:
      currentState = State.ACT;
      break;
    case 2:
      currentState = State.NSW;
      break;
    case 3:
      currentState = State.NT;
      break;
    case 4:
      currentState = State.QLD;
      break;
    case 5:
      currentState = State.SA;
      break;
    case 6:
      currentState = State.TAS;
      break;
    case 7:
      currentState = State.VIC;
      break;
    case 8:
      currentState = State.WA;
      break;
    default:
      break;
  }
  return currentState;
};

/**
 * Convert dhl_eligibility code from CRM to eligibility type name
 * @param {Number} eligibilityCode Value of dhl_eligibility CRM enum. Eg, 0, 1, 2, 3
 * @returns {string|null} Eligibility type name for the given code. Eg, ADF, Ex-Serving, etc
 */
export const mapCrmEligibilityCodeToName = (eligibilityCode) => {
  let currentEligibility = null;
  switch (eligibilityCode) {
    case 0:
      currentEligibility = EligibilityTypes.SERVING[0];
      break;
    case 1:
      currentEligibility = EligibilityTypes.EX_SERVING[0];
      break;
    case 2:
      currentEligibility = EligibilityTypes.WORK_CONNECTION[0];
      break;
    case 3:
      currentEligibility = EligibilityTypes.RELATED[0];
      break;
    default:
      break;
  }
  return currentEligibility;
};